import React from 'react';
import cuid from 'cuid';
import sortBy from 'lodash/sortBy';
import useEntity from '@/lib/hooks/swr/useEntity';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import { useCurrentUser } from '@/lib/hooks/userData';
import {
  addConstituentToLifecycleBoard,
  removeConstituentFromLifecycleBoard,
  updateConstituentOnLifecycleBoard,
} from '@/services/constituentServices';

const useLifecycleBoard = (boardId, swrOptions) => {
  const { data, mutate, ...rest } = useEntity(boardId, 'board', swrOptions);
  const [addedColumns, setAddedColumns] = React.useState([]);
  const existingColumns = React.useRef([]);
  const ownerOrgId = useOwnerOrgId();
  const {
    user: { displayName, userId },
  } = useCurrentUser(true);

  React.useEffect(() => {
    setAddedColumns([]);
  }, [boardId]);

  // This fixes a bug where `removeColumn` could be called with a stale
  // data.columns state. Using a ref ensures a current reference.
  React.useEffect(() => {
    if (!data) return;
    existingColumns.current = data.columns || [];
  }, [data]);

  const addColumn = (index) => {
    const temporaryId = cuid();
    setAddedColumns([
      ...addedColumns,
      {
        columnDescription: '',
        columnId: temporaryId,
        columnTitle: '',
        index,
        isNew: true,
      },
    ]);
  };

  const addConstituent = ({ constituentId, columnId }) =>
    addConstituentToLifecycleBoard({
      boardId: data?.boardId,
      columnId,
      constituentId,
      createdBy: { displayName, userId },
      ownerOrgId,
    });

  const removeConstituent = (constituentId) =>
    removeConstituentFromLifecycleBoard({
      boardId: data?.boardId,
      constituentId,
    });

  const updateConstituent = ({ constituentId, columnId }) =>
    updateConstituentOnLifecycleBoard({
      boardId: data?.boardId,
      columnId,
      constituentId,
    });

  const removeColumn = async (columnId) => {
    if (addedColumns.some((col) => col.columnId === columnId)) {
      setAddedColumns(addedColumns.filter((col) => col.columnId !== columnId));
    } else {
      const columns = existingColumns.current
        .filter((col) => columnId !== col.columnId)
        .map((col, i) => ({ ...col, index: i })); // Reset the index numbers.
      await mutate({ columns });
    }
  };

  const updateColumnInfo = ({ columnId, index, isNew }, patch) => {
    let columns;
    if (isNew) {
      columns = [...existingColumns.current];
      columns.splice(index, 0, patch);
      // If adding a new column, remove the old temporary copy of that column.
      setAddedColumns(
        addedColumns.filter((column) => column.columnId !== columnId),
      );
    } else {
      columns = existingColumns.current.map((col) =>
        col.columnId === columnId ? { ...col, ...patch } : col,
      );
    }
    // Reset the index numbers.
    columns = columns.map((col, i) => ({ ...col, index: i }));
    return mutate({ columns });
  };

  const columns = React.useMemo(
    () =>
      sortBy([...(data?.columns || []), ...addedColumns], ['index', 'isNew']),
    [data, addedColumns],
  );

  return {
    addColumn,
    addConstituent,
    columns,
    data,
    mutate,
    removeColumn,
    removeConstituent,
    updateColumnInfo,
    updateConstituent,
    ...rest,
  };
};

export default useLifecycleBoard;
